import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ShoppingBagIcon,
  UserIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  ReceiptRefundIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import {
  FULFILLMENT_LABELS,
  FULFILLMENT_COLORS,
  formatMoney,
} from '../../types/hardwareOrders';

// Refund detail lives in raw_payload (Admin GraphQL shape, captured by the
// backfill script). Parsed defensively — webhook-ingested orders have no
// refunds at orders/paid time, so absence is normal.
interface RefundLineItemView {
  name: string;
  quantity: number;
  amount: string | null;
}
interface RefundView {
  id: string;
  createdAt: string | null;
  note: string | null;
  amount: string | null;
  lineItems: RefundLineItemView[];
}

function parseRefunds(payload: unknown): {
  refunds: RefundView[];
  totalRefunded: string | null;
  netPayment: string | null;
} {
  const p = payload as any;
  const refunds: RefundView[] = Array.isArray(p?.refunds)
    ? p.refunds.map(
        (r: any, i: number): RefundView => ({
          id: r?.id ?? String(i),
          createdAt: r?.createdAt ?? null,
          note: r?.note ?? null,
          amount: r?.totalRefundedSet?.shopMoney?.amount ?? null,
          lineItems: (r?.refundLineItems?.nodes ?? []).map((li: any): RefundLineItemView => ({
            name: li?.lineItem?.name ?? '—',
            quantity: li?.quantity ?? 0,
            amount: li?.subtotalSet?.shopMoney?.amount ?? null,
          })),
        }),
      )
    : [];
  return {
    refunds,
    totalRefunded: p?.totalRefundedSet?.shopMoney?.amount ?? null,
    netPayment: p?.netPaymentSet?.shopMoney?.amount ?? null,
  };
}

// Contact detail also lives in raw_payload. Tolerates both the backfill's
// GraphQL camelCase shape and the webhook's REST snake_case shape.
function parseContact(payload: unknown): { phone: string | null; shipping: string[] } {
  const p = payload as any;
  const ship = p?.shippingAddress ?? p?.shipping_address ?? null;
  const phone = p?.phone ?? p?.customer?.phone ?? ship?.phone ?? null;
  const cityLine = ship ? [ship.city, ship.province, ship.zip].filter(Boolean).join(' ') : '';
  const shipping: string[] = ship
    ? [ship.name, ship.company, ship.address1, ship.address2, cityLine, ship.country].filter(
        (x: unknown): x is string => typeof x === 'string' && x.trim() !== '',
      )
    : [];
  return { phone, shipping };
}

// Discounts also live in raw_payload. Tolerates the backfill's GraphQL shape
// (discountApplications.nodes, MoneyV2 / PricingPercentageValue) and the
// webhook's REST shape (discount_applications, value/value_type strings).
interface DiscountView {
  label: string;
  description: string | null;
  amount: string | null;
  percentage: number | null;
}

function parseDiscounts(payload: unknown): { discounts: DiscountView[]; totalDiscounts: string | null } {
  const p = payload as any;
  const totalDiscounts = p?.totalDiscountsSet?.shopMoney?.amount ?? p?.total_discounts ?? null;
  const nodes = p?.discountApplications?.nodes ?? p?.discount_applications ?? [];
  const discounts: DiscountView[] = (Array.isArray(nodes) ? nodes : []).map((d: any): DiscountView => {
    const amount =
      d?.value?.__typename === 'MoneyV2'
        ? d.value.amount
        : d?.value_type === 'fixed_amount'
          ? d?.value
          : null;
    const percentage =
      d?.value?.__typename === 'PricingPercentageValue'
        ? d.value.percentage
        : d?.value_type === 'percentage'
          ? Number(d?.value)
          : null;
    return {
      label: d?.code ?? d?.title ?? 'Discount',
      description: d?.description ?? null,
      amount,
      percentage,
    };
  });
  // Shopify reports a discount total but no application detail — still show a row.
  if (discounts.length === 0 && Number(totalDiscounts ?? 0) > 0) {
    discounts.push({ label: 'Discount', description: null, amount: totalDiscounts, percentage: null });
  }
  return { discounts, totalDiscounts };
}

// Shopify money in raw_payload is a decimal string in major units.
function formatAmount(amount: string | null, currency: string | null): string {
  if (amount == null) return '—';
  const fixed = Number(amount).toFixed(2);
  return currency ? `${fixed} ${currency}` : fixed;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-200 mt-0.5">{children}</dd>
    </div>
  );
}

export function AdminHardwareOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [rawOpen, setRawOpen] = useState(false);

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['admin', 'hardware-order', orderId],
    queryFn: () => api.adminGetHardwareOrder(orderId!),
    enabled: !!orderId,
  });

  if (isLoading) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  if (isError || !order) return <div className="p-8 text-center text-red-400">Order not found.</div>;

  const items = order.items ?? [];
  const unmatched = items.filter((it) => it.sku && !it.product_id).length;
  const { refunds, totalRefunded, netPayment } = parseRefunds(order.raw_payload);
  const hasRefunds = refunds.length > 0 || Number(totalRefunded ?? 0) > 0;
  const { phone, shipping } = parseContact(order.raw_payload);
  const { discounts, totalDiscounts } = parseDiscounts(order.raw_payload);
  const singleDiscount = discounts.length === 1;

  return (
    <div className="space-y-6">
      <Link to="/admin/hardware-orders" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200">
        <ArrowLeftIcon className="w-4 h-4" /> Back to hardware orders
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShoppingBagIcon className="w-6 h-6 text-cyan-400" />
            {order.shopify_order_number ?? order.shopify_order_id}
          </h1>
          <p className="text-sm text-slate-400 mt-1 font-mono">Shopify order {order.shopify_order_id}</p>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium ${FULFILLMENT_COLORS[order.fulfillment_status]}`}>
          {FULFILLMENT_LABELS[order.fulfillment_status]}
        </span>
      </div>

      {unmatched > 0 && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm">
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
          {unmatched} line item{unmatched > 1 ? 's have' : ' has'} an unmatched SKU — fix the SKU mapping or fulfill manually.
        </div>
      )}

      {/* Summary + customer */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white mb-3">Order</h2>
          <dl className="grid grid-cols-2 gap-y-3 gap-x-4">
            <Field label="Total">
              <span className="font-mono">{formatMoney(order.total_cents, order.currency)}</span>
            </Field>
            <Field label="Payment">{order.financial_status?.replace(/_/g, ' ') ?? '—'}</Field>
            {hasRefunds && (
              <>
                <Field label="Refunded">
                  <span className="font-mono text-red-400">−{formatAmount(totalRefunded, order.currency)}</span>
                </Field>
                <Field label="Net paid">
                  <span className="font-mono">{formatAmount(netPayment, order.currency)}</span>
                </Field>
              </>
            )}
            <Field label="Created">
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="w-3.5 h-3.5 text-slate-500" />
                {new Date(order.created_at).toLocaleString('en-NZ')}
              </span>
            </Field>
            <Field label="Updated">{new Date(order.updated_at).toLocaleString('en-NZ')}</Field>
          </dl>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white mb-3">Customer</h2>
          <dl className="space-y-3">
            <Field label="Email">{order.email ?? '—'}</Field>
            {phone && (
              <Field label="Phone">
                <a href={`tel:${phone}`} className="hover:text-cyan-300">{phone}</a>
              </Field>
            )}
            {shipping.length > 0 && (
              <Field label="Shipping address">
                {shipping.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </Field>
            )}
            <Field label="Platform user">
              {order.user_id ? (
                <Link
                  to={`/admin/users?search=${encodeURIComponent(order.email ?? '')}&expand=${encodeURIComponent(order.user_id)}`}
                  className="inline-flex items-center gap-1.5 text-green-400 hover:text-green-300"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  Linked
                  <span className="font-mono text-xs text-slate-400">({order.user_id})</span>
                </Link>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-slate-400">
                  <UserIcon className="w-4 h-4" />
                  Not linked to a platform user
                </span>
              )}
            </Field>
          </dl>
        </div>
      </div>

      {/* Line items */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <h2 className="text-sm font-semibold text-white px-5 pt-5 pb-3">Line items</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-700">
                <th className="px-5 py-2 font-medium">Title</th>
                <th className="px-5 py-2 font-medium">SKU</th>
                <th className="px-5 py-2 font-medium text-right">Qty</th>
                <th className="px-5 py-2 font-medium text-right">Unit price</th>
                <th className="px-5 py-2 font-medium">Product match</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {items.map((it) => (
                <tr key={it.id}>
                  <td className="px-5 py-2.5 text-slate-200">{it.title ?? '—'}</td>
                  <td className="px-5 py-2.5 font-mono text-slate-400">{it.sku ?? '—'}</td>
                  <td className="px-5 py-2.5 text-right text-slate-300">{it.quantity}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-slate-300">{formatMoney(it.unit_price_cents, order.currency)}</td>
                  <td className="px-5 py-2.5">
                    {it.product_id ? (
                      <span className="inline-flex items-center gap-1 text-green-400">
                        <CheckCircleIcon className="w-4 h-4" /> Matched
                      </span>
                    ) : it.sku ? (
                      <span className="inline-flex items-center gap-1 text-amber-400">
                        <ExclamationTriangleIcon className="w-4 h-4" /> Unmatched
                      </span>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {discounts.map((d, i) => (
                <tr key={`discount-${i}`} className="bg-emerald-500/5">
                  <td className="px-5 py-2.5 text-emerald-300">
                    <span className="inline-flex items-center gap-1.5">
                      <TagIcon className="w-4 h-4" />
                      Discount — {d.label}
                      {d.percentage != null && ` (${d.percentage}% off)`}
                    </span>
                    {d.description && <div className="text-xs text-slate-400 mt-0.5">{d.description}</div>}
                  </td>
                  <td className="px-5 py-2.5 font-mono text-slate-500">—</td>
                  <td className="px-5 py-2.5 text-right text-slate-500">—</td>
                  <td className="px-5 py-2.5 text-right font-mono text-emerald-300">
                    {/* Percentage-only application: the money value is the order-level total */}
                    −{formatAmount(d.amount ?? (singleDiscount ? totalDiscounts : null), order.currency)}
                  </td>
                  <td className="px-5 py-2.5 text-slate-500">—</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refunds — parsed from raw_payload; only rendered when the order has any */}
      {hasRefunds && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <ReceiptRefundIcon className="w-4 h-4 text-red-400" />
              Refunds
            </h2>
            <span className="text-sm font-mono text-red-400">−{formatAmount(totalRefunded, order.currency)}</span>
          </div>
          <div className="divide-y divide-slate-700/50">
            {refunds.map((refund) => (
              <div key={refund.id} className="px-5 py-3 flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-slate-200">
                    {refund.createdAt ? new Date(refund.createdAt).toLocaleString('en-NZ') : '—'}
                  </div>
                  {refund.note && <div className="text-xs text-slate-400 mt-0.5">“{refund.note}”</div>}
                  {refund.lineItems.length > 0 && (
                    <ul className="text-xs text-slate-400 mt-1 space-y-0.5">
                      {refund.lineItems.map((li, i) => (
                        <li key={i}>
                          {li.quantity} × {li.name}
                          {li.amount != null && (
                            <span className="font-mono"> ({formatAmount(li.amount, order.currency)})</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <span className="text-sm font-mono text-red-400 shrink-0">
                  −{formatAmount(refund.amount, order.currency)}
                </span>
              </div>
            ))}
            {refunds.length === 0 && (
              <div className="px-5 py-3 text-xs text-slate-500">
                Refund total reported by Shopify, but per-refund detail is not in the stored payload.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Raw Shopify payload — all detail collected via the ordering process */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <button
          onClick={() => setRawOpen((o) => !o)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-700/30"
        >
          <span className="text-sm font-semibold text-white">Raw Shopify payload</span>
          {rawOpen ? <ChevronDownIcon className="w-4 h-4 text-slate-400" /> : <ChevronRightIcon className="w-4 h-4 text-slate-400" />}
        </button>
        {rawOpen && (
          <pre className="px-5 pb-5 text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap break-words">
            {JSON.stringify(order.raw_payload, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
